import Footer from '../components/Footer';
import Head from 'next/head';
import { MainWrapper } from './styles';
import Navbar from '@/components/Nav/NavBar';
import React from 'react';
import { useRouter } from 'next/router';
import SiteConfig from '../config';

// Authoritative origin for canonical/og:url tags (no trailing slash).
const SITE_ORIGIN = SiteConfig.url.replace(/\/$/, '');

// Branded share card used whenever a page supplies no image of its own. LinkedIn and X
// both need an absolute URL and at least 1200x627 to render a large card rather than a
// thumbnail, so the default is sized to match what the page templates emit.
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/img/og-default.png`;

// Exported so page templates can request the same crop from the Sanity CDN.
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

interface LayoutProps {
  metaTitle?: string;
  metaKeywords?: string;
  metaDescription?: string;
  navIsFixed?: boolean;
  navIsDark?: boolean;
  isHomepage?: boolean;
  children?: React.ReactNode;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogUrl?: string;
  noCanonical?: boolean;
  noHelmet?: boolean;
  menuData?: unknown;
  footer?: unknown;
  bgWhite?: boolean;
  noMargin?: boolean;
}

const TemplateWrapper: React.FC<LayoutProps> = ({
  metaTitle,
  metaKeywords,
  metaDescription,
  bgWhite,
  noMargin = false,
  navIsFixed,
  navIsDark,
  isHomepage,
  children,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogUrl,
  noCanonical = false,
  noHelmet = false,
  menuData,
  footer,
}) => {
  const { title, description, keywords: configKeywords, social } = SiteConfig;
  const router = useRouter();
  const keywords = metaKeywords || configKeywords;
  const ogTitle = metaTitle || title;
  const ogDescription = metaDescription || description;
  // Crawlers reject relative og:image values, so promote a site-relative path to an
  // absolute URL and fall back to the branded card when the page passes nothing.
  const ogImageUrl = ogImage ? (ogImage.startsWith('/') ? `${SITE_ORIGIN}${ogImage}` : ogImage) : DEFAULT_OG_IMAGE;
  const ogImageWidthInternal = ogImage ? ogImageWidth : OG_IMAGE_WIDTH;
  const ogImageHeightInternal = ogImage ? ogImageHeight : OG_IMAGE_HEIGHT;

  // Self-referencing canonical URL: use the explicit ogUrl when provided,
  // otherwise derive it from the current path with any query string/hash
  // stripped, so /page, /page?utm=x and /page#section all canonicalize to /page.
  const path = router.asPath.split(/[?#]/)[0];
  const canonicalUrl = ogUrl || `${SITE_ORIGIN}${path === '/' ? '' : path}`;

  return (
    <MainWrapper bgWhite={bgWhite}>
      {!noHelmet && (
        <Head>
          <title>{ogTitle}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
          <meta name="description" content={ogDescription} />
          <meta name="keywords" content={keywords} />
          <link rel="apple-touch-icon" sizes="180x180" href="/img/apple-touch-icon.png" />
          <link rel="icon" type="image/png" href="/img/favicon-32x32.png" sizes="32x32" />
          <link rel="icon" type="image/png" href="/img/favicon-16x16.png" sizes="16x16" />

          <meta name="theme-color" content="#fff" />

          <meta property="og:type" content="business.business" />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          {!noCanonical && <link rel="canonical" href={canonicalUrl} />}
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:image" content={ogImageUrl} />
          {ogImageWidthInternal && <meta property="og:image:width" content={String(ogImageWidthInternal)} />}
          {ogImageHeightInternal && <meta property="og:image:height" content={String(ogImageHeightInternal)} />}

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={ogTitle} />
          <meta name="twitter:description" content={ogDescription} />
          <meta name="twitter:image" content={ogImageUrl} />
          <meta property="fb:app_id" content="1516929418476527" />
          <meta
            property="business:contact_data:street_address"
            content="211 W Wacker Dr Ste 120 #1090, Chicago, IL 60606"
          />
          <meta property="business:contact_data:locality" content="Chicago" />
          <meta property="business:contact_data:postal_code" content="60622" />
          <meta property="business:contact_data:country_name" content="United States" />
          <meta property="business:contact_data:email" content="hello@foxbox.com" />
          <meta property="business:contact_data:phone_number" content="312-416-9997" />
          <meta property="business:contact_data:website" content="https://foxbox.com" />
        </Head>
      )}
      <Navbar
        fixed={navIsFixed}
        isDark={navIsDark}
        footer={footer}
        social={social}
        menuData={menuData}
        isHomepage={isHomepage}
        noMargin={noMargin}
      />
      <main id="page-wrap" role="main">
        {children}
      </main>
      <Footer social={social} footerLinks={footer} />
    </MainWrapper>
  );
};

export default TemplateWrapper;
